import { useCallback, useEffect, useMemo, useState } from "react";
import { GetReportJobStatusResponse } from "../../../../reports/data/daos/read/get_report_job_status";
import { useDependencyInjection } from "../../../../../config/di/dependency_injection_context";
import { processError } from "../../../../../common/utils/base_error";
import { usePageLoader } from "../../../../../common/contexts/page_content_context";
import { MapaConcretagemFiltro } from "../../../domain/models/mapa_concretagem_filtro";
import { ReportChecklistForm } from "../../schemas/Obras_informacoesgerais/report_checklist";
import { ReportMapaConcretagemForm, ReportMapaConcretagemColumnsForm } from "../../schemas/Obras_informacoesgerais/report_mapa_concretagem";
import { ReportMonitoramentoChecklistForm } from "../../schemas/Obras_informacoesgerais/report_monitoramento_checklist";
import { useUserAuthStore } from "../../../../../stores/global/userAuth";
import { ReportViewerResponse, useReportViewer } from "../../../../../config/hooks/utils/report_viewer";
import { useCachedDataLoader } from "../../../../../common/hooks/use_cached_data_loader";
import { FornecedorDropDown } from "../../../../../common/domain/models/fornecedor_dropdown";
import { ObraAreaDropDown } from "../../../domain/models/obra_area_dropdown";
import { ObraServicoDropDown } from "../../../domain/models/obra_servico_dropdown";
import { useDropDownLoader } from "../../../../../common/hooks/use_dropdown_loader";
import { MonitoramentoChecklistEmpresa } from "../../../domain/models/monitoramento_checklist_empresa";
import { MonitoramentoChecklistFuncionario } from "../../../domain/models/monitoramento_checklist_funcionario";

import CommonConstants from "../../../../../common/constants";

/**
 * ObrasCommonReportsState
 */

export type ObrasCommonReportsState = {
  saving: boolean;
  loading: boolean;
  openReportMapaConcretagemModal: boolean;
  openReportMonitoramentoChecklistModal: boolean;
  openReportChecklistModal: boolean;
  openReportJobWaiterModal: boolean;
  reportJobResult: GetReportJobStatusResponse | null;
  fornecedoresDropDown: FornecedorDropDown[];
  obraAreasDropDown: ObraAreaDropDown[];
  obraServicosDropDown: ObraServicoDropDown[];
  monitoramentoChecklistEmpresasDropDown: MonitoramentoChecklistEmpresa[];
  monitoramentoChecklistFuncionariosDropDown: MonitoramentoChecklistFuncionario[];
  filtroMapaConcretagem: MapaConcretagemFiltro | undefined;
  popperRelatorio: { id: number; label: string; }[];
  reportViewerState: ReportViewerResponse;
  handlePopperRelatorioSelect: (idOption: number) => Promise<void>;
  handleReportMapaConcretagemModalOpenState: (openState: boolean) => void;
  handleReportMapaConcretagemSubmit: (form: ReportMapaConcretagemForm) => Promise<void>;
  handleReportMapaConcretagemSubmitColumns: (colunas: ReportMapaConcretagemColumnsForm) => Promise<void>;
  handleReportMonitoramentoChecklistModalOpenState: (openState: boolean) => void;
  handleReportMonitoramentoChecklistSubmit: (form: ReportMonitoramentoChecklistForm) => Promise<void>;
  handleReportChecklistModalOpenState: (openState: boolean) => void;
  handleReportChecklistSubmit: (form: ReportChecklistForm, areaName: string | null) => Promise<void>;
  handleCloseReportJobWaiterModal: () => void;
};

/**
 * useObrasCommonReports
 */

type Props = {
  idObra: number | undefined;
};

export const useObrasCommonReports = ({
  idObra,
}: Props): [ObrasCommonReportsState, (idObra?: number) => Promise<void>] => {
  const {
    reports: {
      getReportPlanoQualidadeObraUseCase,
      getReportControleSustentabilidadeUseCase,
      getReportMapaConcretagemUseCase,
      getReportMonitoramentoChecklistUseCase,
      getReportObraChecklistUseCase,
      getReportJobStatusUseCase,
    },
    obras: {
      getObraAreaDropDownUseCase,
      getObraServicoDropDownUseCase,
      getMonitoramentoChecklistEmpresaDropDownUseCase,
      getMonitoramentoChecklistFuncionarioDropDownUseCase,
      getMapaConcretagemFiltroUseCase,
      saveMapaConcretagemFiltroUseCase,
    },
  } = useDependencyInjection();

  const {
    loadFornecedoresDropDown,
  } = useDropDownLoader();

  const { idUsuario } = useUserAuthStore();

  const { pushErrorNotification } = usePageLoader();

  const [saving, setSaving] = useState(false);

  const [jobId, setJobId] = useState<string>();
  const [reportJobResult, setReportJobResult] = useState<GetReportJobStatusResponse | null>(null);

  const [openReportMapaConcretagemModal, setOpenReportMapaConcretagemModal] = useState(false);
  const [openReportMonitoramentoChecklistModal, setOpenReportMonitoramentoChecklistModal] = useState(false);
  const [openReportChecklistModal, setOpenReportChecklistModal] = useState(false);
  const [openReportJobWaiterModal, setOpenReportJobWaiterModal] = useState(false);

  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const finishJob = () => {
      setJobId(undefined);
      setOpenReportJobWaiterModal(false);
    };

    const checkJobStatus = async () => {
      try {
        const result = await getReportJobStatusUseCase.getReportJobStatus(jobId);
        if (cancelled) return;

        if (result.status === "Concluido") {
          setReportJobResult(result);
          setJobId(undefined);
        } else if (result.status === "Falhou") {
          finishJob();
          pushErrorNotification("Erro", result.mensagem || "Falha ao gerar relatório!");
        } else {
          timer = setTimeout(checkJobStatus, 30000);
        }
      } catch (e: unknown) {
        if (cancelled) return;
        finishJob();
        pushErrorNotification("Erro", processError(e, "Falha ao consultar status do relatório!"));
      }
    };

    timer = setTimeout(checkJobStatus, 5000);

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [
    jobId,
    getReportJobStatusUseCase,
    pushErrorNotification,
  ]);

  const [fornecedoresDropDown, setFornecedoresDropDown] = useState<FornecedorDropDown[]>();
  const [obraAreasDropDown, setObraAreasDropDown] = useState<ObraAreaDropDown[]>();
  const [obraServicosDropDown, setObraServicosDropDown] = useState<ObraServicoDropDown[]>();
  const [monitoramentoChecklistEmpresaDropDown, setMonitoramentoChecklistEmpresaDropDown] = useState<MonitoramentoChecklistEmpresa[]>();
  const [monitoramentoChecklistFuncionarioDropDown, setMonitoramentoChecklistFuncionarioDropDown] = useState<MonitoramentoChecklistFuncionario[]>();

  const [filtroMapaConcretagem, setFiltroMapaConcretagem] = useState<MapaConcretagemFiltro>();

  const popperRelatorio = useMemo(() => {
    return [
      { id: 1, label: "Mapa de Concretagem" },
      { id: 2, label: "Plano de Qualidade" },
      { id: 3, label: "Controle de Sustentabilidade" },
      { id: 4, label: "Monitoramento de Checklist" },
      { id: 5, label: "Exportar Relatório Checklists" },
    ];
  }, [
  ]);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const loadObraAreaDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.obraAreaDropDown,
    loader: async (idObra: number) => await getObraAreaDropDownUseCase.getObraAreaDropDown(idObra),
    onSet: dt => setObraAreasDropDown(dt),
    onError: e => { throw e; },
  });

  const loadObraServicoDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.obraServicoDropDown,
    loader: async (idObra: number) => await getObraServicoDropDownUseCase.getObraServicoDropDown(idObra),
    onSet: dt => setObraServicosDropDown(dt),
    onError: e => { throw e; },
  });

  const loadMonitoramentoChecklistEmpresaDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.monitoramentoChecklistEmpresaDropDown,
    loader: async (idObra: number) => await getMonitoramentoChecklistEmpresaDropDownUseCase
      .getMonitoramentoChecklistEmpresaDropDown(idObra),
    onSet: dt => setMonitoramentoChecklistEmpresaDropDown(dt),
    onError: e => { throw e; },
  });

  const loadMonitoramentoChecklistFuncionarioDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.monitoramentoChecklistFuncionarioDropDown,
    loader: async (idObra: number) => await getMonitoramentoChecklistFuncionarioDropDownUseCase
      .getMonitoramentoChecklistFuncionarioDropDown(idObra),
    onSet: dt => setMonitoramentoChecklistFuncionarioDropDown(dt),
    onError: e => { throw e; },
  });

  const loadMapaConcretagemFiltro = useCallback(async () => {
    try {
      const filtros = await getMapaConcretagemFiltroUseCase.getMapaConcretagemFiltro();
      setFiltroMapaConcretagem(filtros ?? undefined);
    } catch (e: unknown) {
      setFiltroMapaConcretagem(undefined);
      throw e;
    }
  }, [
    getMapaConcretagemFiltroUseCase,
  ]);

  const loadObrasCommonReportsData = useCallback(async (id?: number) => {
    const finalId = id ?? idObra;
    if (finalId) {
      await loadFornecedoresDropDown(dt => setFornecedoresDropDown(dt));
      await loadObraAreaDropdown(finalId);
      await loadObraServicoDropdown(finalId);
      await loadMonitoramentoChecklistEmpresaDropDown(finalId);
      await loadMonitoramentoChecklistFuncionarioDropDown(finalId);
      await loadMapaConcretagemFiltro();
    }
  }, [
    idObra,
    loadFornecedoresDropDown,
    loadObraAreaDropdown,
    loadObraServicoDropdown,
    loadMonitoramentoChecklistEmpresaDropDown,
    loadMonitoramentoChecklistFuncionarioDropDown,
    loadMapaConcretagemFiltro,
  ]);

  const handlePopperRelatorioSelect = useCallback(async (idOption: number) => {
    if (idObra) {
      switch (idOption) {
        case 1: /* Mapa Concretagem */
          setOpenReportMapaConcretagemModal(true);
          break;

        case 2: /* Plano de Qualidade */
          try {
            setSaving(true);
            await startReportViewerProcess({
              fetchCallback: async (requiredMimeType) =>
                await getReportPlanoQualidadeObraUseCase.getReportPlanoQualidadeObra(requiredMimeType, idObra)
            });
          } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
          } finally {
            setSaving(false);
          }
          break;

        case 3: /* Controle de Sustentabilidade */
          try {
            setSaving(true);
            await startReportViewerProcess({
              fetchCallback: async (requiredMimeType) =>
                await getReportControleSustentabilidadeUseCase.getReportControleSustentabilidade(requiredMimeType, idObra)
            });
          } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
          } finally {
            setSaving(false);
          }
          break;

        case 4: /* Monitoramento de Checklist */
          setOpenReportMonitoramentoChecklistModal(true);
          break;

        case 5: /* Exportar Relatório Checklists */
          setOpenReportChecklistModal(true);
          break;
      }
    }
  }, [
    idObra,
    getReportPlanoQualidadeObraUseCase,
    getReportControleSustentabilidadeUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleReportMapaConcretagemModalOpenState = useCallback((openState: boolean) => {
    setOpenReportMapaConcretagemModal(openState);
  }, [
  ]);

  const handleReportMapaConcretagemSubmit = useCallback(async (form: ReportMapaConcretagemForm) => {
    if (idObra) {
      try {
        setSaving(true);

        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportMapaConcretagemUseCase.getReportMapaConcretagem(
              requiredMimeType,
              idObra,
              form.dataInicio,
              form.dataFim,
              form.fornecedores,
              form.localLancamento,
              form.resistencia,
              form.nroNotaFiscal,
              form.statusRompimento,
              form.enviadoLaboratorio,
              form.colunas,
            ),
          closeReportModalCallback: () => {
            setOpenReportMapaConcretagemModal(false);
          }
        });

      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatorio!'));
      } finally {
        setSaving(false);
      }
    }
  }, [
    idObra,
    getReportMapaConcretagemUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleReportMapaConcretagemSubmitColumns = useCallback(async (colunas: ReportMapaConcretagemColumnsForm) => {
    try {
      if (idObra) {
        setSaving(true);

        await saveMapaConcretagemFiltroUseCase.saveMapaConcretagemFiltro({ idObra, ...colunas });
        await loadMapaConcretagemFiltro();
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao salvar filtro de mapa de concretagem!'));
    } finally {
      setSaving(false);
    }
  }, [
    idObra,
    saveMapaConcretagemFiltroUseCase,
    loadMapaConcretagemFiltro,
    pushErrorNotification,
  ]);

  const handleReportMonitoramentoChecklistModalOpenState = useCallback((openState: boolean) => {
    setOpenReportMonitoramentoChecklistModal(openState);
  }, [
  ]);

  const handleReportMonitoramentoChecklistSubmit = useCallback(async (form: ReportMonitoramentoChecklistForm) => {
    if (idObra && idUsuario) {
      try {
        setSaving(true);
        await startReportViewerProcess({
          fetchCallback: async (mimeType) =>
            await getReportMonitoramentoChecklistUseCase.getReportMonitoramentoChecklist(
              idObra,
              idUsuario,
              form.area,
              form.statusArea,
              form.servico,
              form.statusServico,
              form.dataAberturaDe,
              form.dataAberturaAte,
              form.dataEncerramentoDe,
              form.dataEncerramentoAte,
              form.statusVerificacao,
              form.statusItemChecklist,
              form.empresaTercerizada,
              form.funcionarios,
              mimeType,
              monitoramentoChecklistEmpresaDropDown ?? [],
              monitoramentoChecklistFuncionarioDropDown ?? [],
            ),
          closeReportModalCallback: () =>
            setOpenReportMonitoramentoChecklistModal(false),
        });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatorio!'));
      } finally {
        setSaving(false);
      }
    }
  }, [
    idObra,
    idUsuario,
    getReportMonitoramentoChecklistUseCase,
    monitoramentoChecklistEmpresaDropDown,
    monitoramentoChecklistFuncionarioDropDown,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleReportChecklistModalOpenState = useCallback((openState: boolean) => {
    setOpenReportChecklistModal(openState);
  }, [
  ]);

  const handleReportChecklistSubmit = useCallback(async (form: ReportChecklistForm, areaName: string | null) => {
    if (jobId) {
      setOpenReportJobWaiterModal(true);
      return;
    }

    try {
      setSaving(true);

      const { operacaoId } = await getReportObraChecklistUseCase.getReportObraChecklist(
        null,
        form.area,
        areaName ?? null,
        form.servico,
      );

      if (!operacaoId) throw new Error("Operação do relatório não informada!");
      setReportJobResult(null);
      setJobId(operacaoId);
      setOpenReportChecklistModal(false);
      setOpenReportJobWaiterModal(true);

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatorio!'));
    } finally {
      setSaving(false);
    }
  }, [
    getReportObraChecklistUseCase,
    jobId,
    pushErrorNotification,
  ]);

  const handleCloseReportJobWaiterModal = useCallback(() => {
    setJobId(undefined);
    setReportJobResult(null);
    setOpenReportJobWaiterModal(false);
  }, [
  ]);

  return [
    {
      loading: saving,
      saving,
      openReportMapaConcretagemModal,
      openReportMonitoramentoChecklistModal,
      openReportChecklistModal,
      openReportJobWaiterModal,
      reportJobResult,
      fornecedoresDropDown: fornecedoresDropDown ?? [],
      obraAreasDropDown: obraAreasDropDown ?? [],
      obraServicosDropDown: obraServicosDropDown ?? [],
      monitoramentoChecklistEmpresasDropDown: monitoramentoChecklistEmpresaDropDown ?? [],
      monitoramentoChecklistFuncionariosDropDown: monitoramentoChecklistFuncionarioDropDown ?? [],
      filtroMapaConcretagem,
      reportViewerState,
      popperRelatorio,
      handlePopperRelatorioSelect,
      handleReportMapaConcretagemModalOpenState,
      handleReportMapaConcretagemSubmit,
      handleReportMapaConcretagemSubmitColumns,
      handleReportMonitoramentoChecklistModalOpenState,
      handleReportMonitoramentoChecklistSubmit,
      handleReportChecklistModalOpenState,
      handleReportChecklistSubmit,
      handleCloseReportJobWaiterModal,
    },
    loadObrasCommonReportsData,
  ];
}
